
import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Activity } from '@/types/program';

export const useActivityManagement = (programId?: string) => {
  const [activities, setActivities] = useState<Activity[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchActivities = useCallback(async () => {
    if (!programId) {
      setActivities([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      
      const { data, error } = await supabase
        .from('activities')
        .select('*')
        .eq('program_id', programId)
        .order('order_index', { ascending: true });

      if (error) throw error;
      setActivities(data || []);
    } catch (error) {
      console.error('Error fetching activities:', error);
      toast.error('Failed to load activities');
    } finally {
      setLoading(false);
    }
  }, [programId]);

  const createActivity = async (activityData: Omit<Activity, 'id' | 'created_at' | 'updated_at'>) => {
    try {
      
      const { data, error } = await supabase
        .from('activities')
        .insert([activityData])
        .select()
        .single();

      if (error) throw error;
      
      toast.success('Activity created successfully');
      await fetchActivities();
      return data;
    } catch (error) {
      console.error('Error creating activity:', error);
      toast.error('Failed to create activity');
      throw error;
    }
  };

  const updateActivity = async (id: string, updates: Partial<Activity>) => {
    try {
      
      const { data, error } = await supabase
        .from('activities')
        .update({ ...updates, updated_at: new Date().toISOString() })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      
      toast.success('Activity updated successfully');
      await fetchActivities();
      return data;
    } catch (error) {
      console.error('Error updating activity:', error);
      toast.error('Failed to update activity');
      throw error;
    }
  };

  const deleteActivity = async (id: string) => {
    try {
      
      const { error } = await supabase
        .from('activities')
        .delete()
        .eq('id', id);

      if (error) throw error;
      
      toast.success('Activity deleted successfully');
      await fetchActivities();
    } catch (error) {
      console.error('Error deleting activity:', error);
      toast.error('Failed to delete activity');
      throw error;
    }
  };

  const reorderActivities = async (reorderedActivities: Activity[]) => {
    try {
      const updates = reorderedActivities.map((activity, index) => ({
        id: activity.id,
        order_index: index
      }));

      for (const update of updates) {
        await supabase
          .from('activities')
          .update({ order_index: update.order_index })
          .eq('id', update.id);
      }

      toast.success('Activities reordered successfully');
      await fetchActivities();
    } catch (error) {
      console.error('Error reordering activities:', error);
      toast.error('Failed to reorder activities');
    }
  };

  useEffect(() => {
    fetchActivities();
  }, [fetchActivities]);

  const totalDuration = activities.reduce((sum, activity) => sum + activity.duration, 0);

  return {
    activities,
    loading,
    totalDuration,
    fetchActivities,
    createActivity,
    updateActivity,
    deleteActivity,
    reorderActivities
  };
};
